<script setup>
import { computed, inject, ref } from "vue";
import StopCard from "./StopCard.vue";
import StopDetailModal from "./stop-detail/StopDetailModal.vue";
import StopDateModal from "./stop-detail/StopDateModal.vue";
import StopTimeWindowModal from "./stop-detail/StopTimeWindowModal.vue";
import StopNormalizeAddressModal from "./stop-detail/StopNormalizeAddressModal.vue";
import CreateManualStopModal from "../../stops/components/CreateManualStopModal.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { usePlannerKanban } from "../composables/usePlannerKanban";
import { vSortable } from "../directives/vSortable";

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
  dragDisabled: {
    type: Boolean,
    default: false,
  },
  sortableOptions: {
    type: Object,
    default() {
      return {};
    },
  },
});
const emit = defineEmits(["kanban-action", "toggle-collapse"]);

const {
  unassignedStopsBoard,
  getRouteDragGroup,
  getRouteVehicleContext,
  handleDragStart,
  applyLocalDragUpdate,
} = usePlannerKanban();
const sweetAlert = inject("$swal", null);

function getUnassignedStopActions(stopItem) {
  const isManual = stopItem?.manual_type === "MANUAL";
  const isEntrega = stopItem?.type === "ENTREGA";
  return [
    [
      {
        label: "Detalle (doble clic)",
        event: "detail",
      },
    ],
    isManual
      ? [
          {
            label: "Modificar",
            event: "modifyManual",
          },
          ...(isEntrega
            ? []
            : [
                {
                  label: "Crear entrega",
                  event: "createDeliveryFromPickup",
                },
              ]),
        ]
      : [
          {
            label: "Normalizar Dirección",
            event: "normalizeAddress",
          },
          {
            label: "Modificar Fecha",
            event: "changeDate",
          },
        ],
    [
      {
        label: "Excluir",
        event: "delete",
        type: "danger",
      },
    ],
  ];
}
const selectedStop = ref(null);
const showStopDetailModal = ref(false);
const showStopDateModal = ref(false);
const showStopTimeWindowModal = ref(false);
const showStopNormalizeAddressModal = ref(false);
const showCreateManualStopModal = ref(false);
const selectedManualStopId = ref(null);
const duplicateSourceStopId = ref(null);
const snackbar = ref(null);
const showRecogidas = ref(true);
const showEntregas = ref(true);

function setDragNoSelect(isActive) {
  if (typeof document === "undefined") return;
  if (isActive) {
    document.body?.classList?.add("kanban-drag-no-select");
    return;
  }
  document.body?.classList?.remove("kanban-drag-no-select");
}

function onDragStart(event) {
  setDragNoSelect(true);
  handleDragStart(event);
}

function getBoardSortableOptions() {
  return {
    ...props.sortableOptions,
    group: getRouteDragGroup(unassignedStopsBoard.value),
    animation: 150,
    draggable: ".kanban-dp-item",
    ghostClass: "drag-ghost",
    chosenClass: "drag-chosen",
    dragClass: "drag-active",
    fallbackOnBody: true,
    swapThreshold: 0.65,
    disabled: props.dragDisabled,
    multiDrag: true,
    multiDragKey: "ctrl",
    selectedClass: "sortable-selected",
    filter: ".stop-route-actions-button, .actions-dropdown-menu",
    preventOnFilter: false,
    onStart: onDragStart,
    onEnd: onDragEnd,
  };
}

const visibleUnassignedStopsCount = computed(() => {
  const items = Array.isArray(unassignedStopsBoard.value?.items)
    ? unassignedStopsBoard.value.items
    : [];
  return items.reduce(
    (counter, stopCard) =>
      isStopCardVisible(stopCard) ? counter + 1 : counter,
    0
  );
});

function closeSnackbar() {
  snackbar.value = null;
}

function closeStopDetailModal() {
  showStopDetailModal.value = false;
  if (
    !showStopDateModal.value &&
    !showStopTimeWindowModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStop.value = null;
  }
}

function closeStopDateModal() {
  showStopDateModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopTimeWindowModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStop.value = null;
  }
}

function closeStopTimeWindowModal() {
  showStopTimeWindowModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopDateModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStop.value = null;
  }
}

function closeStopNormalizeAddressModal() {
  showStopNormalizeAddressModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopDateModal.value &&
    !showStopTimeWindowModal.value
  ) {
    selectedStop.value = null;
  }
}

function openUnassignedStopDetail(stopItem) {
  if (!stopItem?._id) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro _id para consultar el detalle de la parada.",
      type: "danger",
      color: "white",
    };
    return;
  }

  selectedStop.value = stopItem;
  showStopDetailModal.value = true;
  showStopDateModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
}

function getStopId(stopItem) {
  return stopItem?.stop_id || stopItem?._id || "";
}

function getStopCardId(stopCard) {
  return stopCard.id;
}

function getStopCardType(stopCard) {
  return stopCard.apiStop.type;
}

function isStopCardVisible(stopCard) {
  const stopType = getStopCardType(stopCard);
  if (stopType === "RECOGIDA") return showRecogidas.value;
  if (stopType === "ENTREGA") return showEntregas.value;
  return false;
}

async function requestDeleteStop(stopItem) {
  const stopId = getStopId(stopItem);
  if (!stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontro stop_id para eliminar la parada.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const confirmOptions = {
    title: "¿Está seguro de querer excluir esta parada?",
    text: "¡No podrá revertir esta acción!",
    showCancelButton: true,
    confirmButtonText: "Aceptar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : {
          isConfirmed: window.confirm(
            "¿Está seguro de querer excluir esta parada?"
          ),
        };
  if (!result?.isConfirmed) return;

  closeSnackbar();
  emit("kanban-action", {
    type: "delete-unassigned-stop",
    payload: {
      stopId,
    },
  });
}

function onUnassignedStopAction(payload) {
  const actionEvent = payload?.action?.event;
  const stopItem = payload?.stopItem;
  if (!stopItem || !actionEvent) return;

  if (actionEvent === "delete") {
    requestDeleteStop(stopItem);
    return;
  }

  if (actionEvent === "detail") {
    openUnassignedStopDetail(stopItem);
    return;
  }

  if (actionEvent === "changeDate") {
    selectedStop.value = stopItem;
    showStopDateModal.value = true;
    showStopTimeWindowModal.value = false;
    showStopNormalizeAddressModal.value = false;
    showStopDetailModal.value = false;
    return;
  }

  if (actionEvent === "modifyManual") {
    const stopId = getStopId(stopItem);
    if (!stopId) {
      snackbar.value = {
        title: "Error",
        message: "No se encontro stop_id para modificar la parada.",
        type: "danger",
        color: "white",
      };
      return;
    }
    selectedManualStopId.value = stopId;
    duplicateSourceStopId.value = null;
    showCreateManualStopModal.value = true;
    return;
  }

  if (actionEvent === "createDeliveryFromPickup") {
    const stopId = getStopId(stopItem);
    if (!stopId) {
      snackbar.value = {
        title: "Error",
        message: "No se encontro stop_id para crear la entrega.",
        type: "danger",
        color: "white",
      };
      return;
    }
    selectedManualStopId.value = null;
    duplicateSourceStopId.value = stopId;
    showCreateManualStopModal.value = true;
    return;
  }

  if (actionEvent === "normalizeAddress") {
    selectedStop.value = stopItem;
    showStopNormalizeAddressModal.value = true;
    showStopTimeWindowModal.value = false;
    showStopDateModal.value = false;
    showStopDetailModal.value = false;
  }
}

function onStopActionSuccess() {
  showStopDateModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
  selectedStop.value = null;
  closeSnackbar();

  emit("kanban-action", {
    type: "refresh-unassigned",
    payload: {},
  });
}

function closeCreateManualStopModal() {
  showCreateManualStopModal.value = false;
  selectedManualStopId.value = null;
  duplicateSourceStopId.value = null;
}

function onCreateManualStopSuccess() {
  const wasEdit = !!selectedManualStopId.value;
  closeCreateManualStopModal();
  closeSnackbar();
  snackbar.value = {
    title: "OK",
    message: wasEdit ? "Parada modificada correctamente" : "Parada creada correctamente",
    type: "info",
    color: "white",
  };
  emit("kanban-action", {
    type: "refresh-unassigned",
    payload: {},
  });
}

function onDragEnd(event) {
  setDragNoSelect(false);
  applyLocalDragUpdate(event);
  emit("kanban-action", {
    type: "drag-drop",
    payload: {
      event,
    },
  });
}
</script>

<template>
  <section
    v-if="unassignedStopsBoard"
    class="kanban-dp-board unassigned-board card"
    :class="{ 'is-collapsed': collapsed }"
  >
    <header class="kanban-dp-header">
      <div
        v-if="!collapsed"
        class="kanban-board-header unassigned-content-card"
      >
        <div class="route-top-row">
          <div class="route-name-pill">
            <div class="route-summary">
              <div class="route-text">
                <div class="route-title-row">
                  <span class="metric-chip unassigned-count">
                    <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                    <span>{{ visibleUnassignedStopsCount }}</span>
                  </span>
                  <span class="route-title unassigned-title-label"
                    >Sin Asignar</span
                  >
                  <button
                    type="button"
                    class="unassigned-toggle-button"
                    aria-label="Ocultar no asignados"
                    @click="emit('toggle-collapse')"
                  >
                    <i class="fas fa-chevron-left" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="route-metrics align-items-center">
          <div class="metrics-row">
            <div
              class="summary-toggle-group"
              role="group"
              aria-label="Filtros de paradas"
            >
              <button
                type="button"
                class="summary-toggle-btn"
                :class="{ 'is-selected': showRecogidas }"
                :aria-pressed="showRecogidas"
                @click="showRecogidas = !showRecogidas"
              >
                <i class="fas fa-arrow-right" aria-hidden="true"></i>
                <span>Recogidas</span>
              </button>

              <button
                type="button"
                class="summary-toggle-btn"
                :class="{ 'is-selected': showEntregas }"
                :aria-pressed="showEntregas"
                @click="showEntregas = !showEntregas"
              >
                <i class="fas fa-arrow-left" aria-hidden="true"></i>
                <span>Entregas</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="unassigned-collapsed-bar">
        <button
          type="button"
          class="unassigned-toggle-button"
          aria-label="Mostrar no asignados"
          @click="emit('toggle-collapse')"
        >
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <div
      v-if="!collapsed"
      v-sortable="getBoardSortableOptions()"
      class="kanban-dp-drag"
      :data-board-id="unassignedStopsBoard.id"
    >
      <div
        v-if="visibleUnassignedStopsCount === 0"
        class="planner-drop-hint"
        aria-hidden="true"
      >
        <span class="planner-drop-hint-label">Arrastra quí las paradas</span>
      </div>
      <article
        v-for="stopCard in unassignedStopsBoard.items"
        v-show="isStopCardVisible(stopCard)"
        :key="getStopCardId(stopCard)"
        class="kanban-dp-item border-radius-md"
        :data-task-id="getStopCardId(stopCard)"
      >
        <stop-card
          :stop-item="stopCard.apiStop"
          :vehicle="getRouteVehicleContext(unassignedStopsBoard)"
          :actions="getUnassignedStopActions(stopCard.apiStop)"
          :show-km="false"
          :show-total-time="false"
          :show-menu="true"
          :menu-disabled="dragDisabled"
          :show-order="false"
          @open="openUnassignedStopDetail"
          @action="onUnassignedStopAction"
        />
      </article>
    </div>
  </section>

  <stop-detail-modal
    v-if="showStopDetailModal && selectedStop"
    modal-id="unassigned-stop-detail-modal"
    :stop-id="selectedStop._id"
    @close="closeStopDetailModal"
  />

  <stop-date-modal
    v-if="showStopDateModal && selectedStop"
    modal-id="unassigned-stop-date-modal"
    :stop-id="selectedStop._id"
    @close="closeStopDateModal"
    @success="onStopActionSuccess"
  />

  <stop-time-window-modal
    v-if="showStopTimeWindowModal && selectedStop"
    modal-id="unassigned-stop-time-window-modal"
    :stop-id="selectedStop._id"
    @close="closeStopTimeWindowModal"
    @success="onStopActionSuccess"
  />

  <stop-normalize-address-modal
    v-if="showStopNormalizeAddressModal && selectedStop"
    modal-id="unassigned-stop-normalize-address-modal"
    :stop-id="selectedStop._id"
    @close="closeStopNormalizeAddressModal"
    @success="onStopActionSuccess"
  />

  <create-manual-stop-modal
    v-if="showCreateManualStopModal"
    :stop-id="selectedManualStopId"
    :duplicate-from-stop-id="duplicateSourceStopId"
    @close="closeCreateManualStopModal"
    @success="onCreateManualStopSuccess"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped lang="scss">
@use "./planner-toggle-shared";
@use "./planner-header-shared";
@use "./planner-surface-shared";

.kanban-dp-board {
  @extend .planner-board-shell;
  box-shadow: inset 0 2px 0 0 transparent,
    inset 0 0 0 1px var(--planner-border, #dbe2ee),
    var(--planner-shadow-sm, 0 1px 2px rgba(16, 24, 40, 0.04));
}

.unassigned-board {
  max-width: var(--planner-board-width, 220px);
  min-width: var(--planner-board-width, 220px);
  width: var(--planner-board-width, 220px);
}

.unassigned-board.is-collapsed {
  flex-basis: 38px;
  max-width: 38px;
  min-width: 38px;
  width: 38px;
}

.kanban-dp-header {
  @extend .planner-board-header-row;
}

.unassigned-board > .kanban-dp-header {
  overflow: hidden;
}

.unassigned-content-card {
  width: 100%;
}

.unassigned-toggle-button {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--planner-border, #dbe2ee);
  border-radius: 6px;
  color: var(--planner-text-soft, var(--color-text-secondary));
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.55rem;
  height: 18px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 18px;
}

.unassigned-title-label {
  flex: 1 1 auto;
  width: 0;
  text-align: center;
}

.unassigned-count {
  position: static;
}

.kanban-dp-drag {
  @extend .planner-drop-surface;
  overflow: visible;
}

.unassigned-board :deep(.kanban-dp-item:hover),
.unassigned-board :deep(.kanban-dp-item:focus-within) {
  z-index: 90;
}

.unassigned-board :deep(.kanban-dp-item.kanban-dropdown-open) {
  z-index: 220;
}

.unassigned-drop-hint {
  display: none;
}

.unassigned-drop-line {
  background: var(--planner-border, #dbe2ee);
  display: block;
  flex: 1 1 auto;
  height: 1px;
}

.unassigned-drop-text {
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.unassigned-collapsed-summary {
  display: none;
}

.unassigned-collapsed-bar {
  align-items: flex-start;
  display: flex;
  justify-content: center;
  min-height: 100%;
  padding-top: 6px;
}

.summary-toggle-group {
  @extend .planner-summary-toggle-group;
  margin: 0 auto;
}

.summary-toggle-btn {
  @extend .planner-summary-toggle-btn;
}

.summary-toggle-btn i {
  font-size: 0.6rem;
}
</style>
